import React from "react";
import { Link } from "react-router-dom";
import { useI18n } from "@/i18n";
import Seo from "@/components/Seo";
import { orgSchema, faqSchema } from "@/lib/seo";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";
import { ArrowRight, Phone } from "@phosphor-icons/react";
import { PHONE_TEL } from "@/lib/contact";

const FAQS = [
  {
    q: { de: "Wie funktioniert die Buchung?", en: "How does booking work?" },
    a: { de: "Geben Sie Abhol- und Zielort, Datum und Uhrzeit ein, wählen Sie Ihre Fahrzeugklasse und bestätigen Sie. Sie erhalten sofort einen Festpreis und eine Buchungsreferenz.", en: "Enter your pickup and destination, date and time, choose your vehicle class and confirm. You instantly receive a fixed price and a booking reference." },
  },
  {
    q: { de: "Ist der Preis wirklich fix?", en: "Is the price really fixed?" },
    a: { de: "Ja. Der angezeigte Preis ist der Endpreis — kein Taxameter, keine versteckten Kosten. Ein transparenter Nachtzuschlag gilt zwischen 22 und 6 Uhr und wird vor der Buchung angezeigt.", en: "Yes. The displayed price is the final price — no meter, no hidden costs. A transparent night surcharge applies between 10 pm and 6 am and is shown before you book." },
  },
  {
    q: { de: "Was passiert, wenn mein Flug Verspätung hat?", en: "What happens if my flight is delayed?" },
    a: { de: "Wir überwachen Ihren Flug rund um die Uhr. Geben Sie einfach Ihre Flugnummer bei der Buchung an — Ihr Chauffeur passt sich automatisch an und Wartezeit bei Verspätung ist kostenlos.", en: "We monitor your flight around the clock. Just add your flight number when booking — your chauffeur adjusts automatically and wait time on delays is free of charge." },
  },
  {
    q: { de: "Wo treffe ich meinen Chauffeur am Flughafen?", en: "Where do I meet my chauffeur at the airport?" },
    a: { de: "Ihr Chauffeur empfängt Sie mit einem Namensschild in der Ankunftshalle des Flughafens Zürich (Meet & Greet).", en: "Your chauffeur greets you with a name sign in the arrivals hall of Zürich Airport (meet & greet)." },
  },
  {
    q: { de: "Wie kann ich bezahlen?", en: "How can I pay?" },
    a: { de: "Sie können online per Karte bezahlen oder bar bzw. per Karte direkt beim Fahrer bei der Ankunft.", en: "You can pay online by card, or with cash or card directly with the driver on arrival." },
  },
  {
    q: { de: "Bieten Sie Kindersitze an?", en: "Do you offer child seats?" },
    a: { de: "Ja, Kindersitze und Sitzerhöhungen stellen wir kostenlos zur Verfügung. Geben Sie einfach die Anzahl und das Alter der Kinder bei der Buchung an.", en: "Yes, we provide child seats and boosters free of charge. Just note the number and age of children when booking." },
  },
  {
    q: { de: "Kann ich meine Buchung stornieren?", en: "Can I cancel my booking?" },
    a: { de: "Ja. Über 'Buchung verwalten' können Sie Ihre Reservierung mit Ihrer Referenz jederzeit ansehen oder stornieren.", en: "Yes. Via 'Manage booking' you can view or cancel your reservation with your reference at any time." },
  },
  {
    q: { de: "Fahren Sie auch in die Berge und ins Ausland?", en: "Do you also drive to the mountains and abroad?" },
    a: { de: "Selbstverständlich. Wir fahren in die gesamte Schweiz — inklusive Bergresorts wie Davos, St. Moritz und Zermatt — sowie ins benachbarte Ausland.", en: "Absolutely. We drive throughout Switzerland — including mountain resorts such as Davos, St. Moritz and Zermatt — as well as to neighbouring countries." },
  },
];

export default function Faq() {
  const { t, lang } = useI18n();
  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-14" data-testid="faq-page">
      <Seo
        title={t("seo_faq_title")}
        description={t("faq_sub")}
        path="/faq"
        jsonLd={[orgSchema(), faqSchema(FAQS.map((f) => ({ q: f.q[lang], a: f.a[lang] })))]}
      />
      <p className="font-body text-sm text-[#EF5A1E] font-semibold mb-3">FAQ</p>
      <h1 className="font-heading font-bold tracking-tight text-4xl sm:text-5xl text-[#0B1533] leading-[1.05]">{t("faq_title")}</h1>
      <p className="font-body text-[#5A607A] mt-5 text-lg">{t("faq_sub")}</p>

      <Accordion type="single" collapsible className="mt-10 space-y-3">
        {FAQS.map((f, i) => (
          <AccordionItem key={i} value={`item-${i}`} data-testid={`faq-item-${i}`}
            className="border border-[#E9E5DE] rounded-2xl px-5 bg-white">
            <AccordionTrigger className="font-heading font-semibold text-base text-[#0B1533] hover:no-underline text-left">
              {f.q[lang]}
            </AccordionTrigger>
            <AccordionContent className="font-body text-[#5A607A] leading-relaxed text-base">
              {f.a[lang]}
            </AccordionContent>
          </AccordionItem>
        ))}
      </Accordion>

      <div className="mt-12 rounded-3xl bg-[#0B1533] p-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-5">
        <div>
          <h3 className="font-heading font-bold text-2xl tracking-tight text-white">{t("contact_title")}</h3>
          <p className="font-body text-white/60 mt-2">{t("contact_sub")}</p>
        </div>
        <div className="flex gap-3 shrink-0">
          <a href={`tel:${PHONE_TEL}`} className="inline-flex items-center gap-2 bg-[#EF5A1E] text-white font-body font-semibold rounded-full px-6 py-3 hover:bg-[#D2450F] transition-colors duration-200"><Phone size={16} weight="fill" /> {t("call_now")}</a>
          <Link to="/contact" className="inline-flex items-center gap-2 border border-white/25 text-white font-body font-semibold rounded-full px-6 py-3 hover:bg-white/10 transition-colors duration-200">{t("nav_contact")} <ArrowRight size={15} /></Link>
        </div>
      </div>
    </div>
  );
}
